// src/DavActivity.js
import React, { useState, useEffect, useCallback } from 'react';
import './DavActivity.css';
import { getRuntimeConfig } from '../config';

const DavActivity = ({ initialTab = 'live' }) => {
  // Enhanced WebDAV Monitor states
  const [isMonitorCollapsed] = useState(false);
  const [webdavLiveFeed, setWebdavLiveFeed] = useState([]);
  const [webdavLogHistory, setWebdavLogHistory] = useState([]);
  const [webdavCurrentTab, setWebdavCurrentTab] = useState(initialTab || 'live');
    useEffect(() => {
    setWebdavCurrentTab(initialTab || 'live');
  }, [initialTab]);

  const [isWebdavLoading, setIsWebdavLoading] = useState(false);
  const [webdavMonitorError, setWebdavMonitorError] = useState(null);
  const [emptyDevices, setEmptyDevices] = useState([]);
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [emptyError, setEmptyError] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [webdavStats, setWebdavStats] = useState({ 
    activeConnections: 0, 
    todayActivityCount: 0,
    availableLogFiles: 0,
    oldestLogDate: null,
    newestLogDate: null,
    retentionDays: 30,
    lastChecked: null 
  });
  const [webdavLogFilters, setWebdavLogFilters] = useState({
    user: '',
    type: '',
    startDate: '',
    endDate: ''
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);

  const token = localStorage.getItem('token');
  const apiUrlBase = `${getRuntimeConfig().API_BASE_URL}/api`;

  // Enhanced WebDAV Log History fetching
  const fetchWebdavLogs = async (filters = webdavLogFilters) => {
    setIsWebdavLoading(true);
    setWebdavMonitorError(null);
    try {
      // Process filters - if startDate is set but endDate is not, set endDate to today
      const processedFilters = { ...filters };
      if (processedFilters.startDate && !processedFilters.endDate) {
        processedFilters.endDate = new Date().toISOString().split('T')[0];
      }
      
      const cleanFilters = Object.fromEntries(
        Object.entries(processedFilters).filter(([_, value]) => value && value.trim() !== '')
      );
      
      const query = new URLSearchParams(cleanFilters).toString();
      const response = await fetch(`${apiUrlBase}/admin/webdav-logs?${query}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Filter out 'browse' activities
      const filteredLogs = (data.logs || []).filter(log => log.type !== 'browse');
      
      setWebdavLogHistory(filteredLogs);
      setAvailableUsers(data.availableUsers || []);
      // Filter out 'browse' from available types
      setAvailableTypes((data.availableTypes || []).filter(type => type !== 'browse'));
      
      console.log(`WebDAV Logs: Loaded ${filteredLogs.length} entries (${data.totalCount || 0} total, browse filtered out)`);
    } catch (err) {
      console.error('Error fetching WebDAV logs:', err);
      setWebdavMonitorError(`Failed to load log history: ${err.message}`);
      setWebdavLogHistory([]);
    } finally {
      setIsWebdavLoading(false);
    }
  };

// --- NEW: Fetch devices whose folders are empty (robust, sorted) ---
const fetchEmptyDevices = (() => {
  let inFlightController = null;

  const sortOwnerThenDevice = (list) => {
    return (list || []).sort((a, b) => {
      const oa = (a.owner_username || 'zzz_no_owner').toLowerCase();
      const ob = (b.owner_username || 'zzz_no_owner').toLowerCase();
      if (oa !== ob) return oa.localeCompare(ob);
      return (a.device_name || '').toLowerCase()
        .localeCompare((b.device_name || '').toLowerCase());
    });
  };

  return async () => {
    try {
      // Cancel any previous request if the tab was switched quickly
      if (inFlightController) inFlightController.abort();
      inFlightController = new AbortController();

      setEmptyLoading(true);
      setEmptyError(null);

      // 12s timeout guard
      const timeout = new Promise((_, rej) =>
        setTimeout(() => rej(new Error('Request timed out')), 12000)
      );

      const res = await Promise.race([
        fetch('/api/admin/empty-devices', {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          signal: inFlightController.signal
        }),
        timeout
      ]);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      setEmptyDevices(sortOwnerThenDevice(data.emptyDevices || []));
    } catch (e) {
      // Ignore aborts from rapid tab changes
      if (e.name === 'AbortError') return;
      console.error('Failed to load empty devices:', e);
      setEmptyError(e.message || 'Failed to load empty devices');
    } finally {
      setEmptyLoading(false);
      inFlightController = null;
    }
  };
})();

  // Enhanced WebDAV Stats fetching
  const fetchWebdavStats = async () => {
    try {
      const response = await fetch(`${apiUrlBase}/admin/webdav-stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setWebdavStats(data);
    } catch (err) {
      console.error('Error fetching WebDAV stats:', err);
    }
  };

  // Function to format the date and time for WebDAV activities
  const formatWebdavActivityTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      const displayHours = hours % 12 || 12;
      return `${day}/${month}/${year} ${displayHours}:${minutes}:${seconds} ${ampm}`;
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Function to format the WebDAV path with bolded user folder
  const formatWebdavPath = (rawPath, username) => {
    const safePath = String(rawPath || '').replace(/\\/g, '/');
    const segs = safePath.replace(/^\/+/, '').split('/').filter(Boolean);

    let owner = username || '';
    let device = '';
    let rest = [];

    if (segs[0] === 'home') {
      // /home/{device}/{...}  → owner comes from username
      device = segs[1] || '';
      rest = segs.slice(2);
    } else if (segs[0] === 'store' && segs[1] === 'homes') {
      // /store/homes/{username}/{device}/{...}
      owner = segs[2] || owner;
      device = segs[3] || '';
      rest = segs.slice(4);
    } else if (segs.length >= 2 && segs[0] === owner) {
      // /{username}/{device}/{...}
      device = segs[1] || '';
      rest = segs.slice(2);
    } else {
      // Fallback: treat first as device
      device = segs[0] || '';
      rest = segs.slice(1);
    }

    const ownerHtml  = owner  ? `<strong class="path-owner">${owner}</strong>` : '';
    const deviceHtml = device ? `/<span class="path-device">${device}</span>` : '';
    const restHtml   = rest.length ? `/<span class="path-rest">${rest.join('/')}</span>` : '';

    return `${ownerHtml}${deviceHtml}${restHtml}`;
  };

  // Removed 'browse' from icons and colors
  const getActivityIcon = (type) => {
    const icons = {
      'upload': '📤',
      'download': '📥', 
      'delete': '🗑️',
      'create_folder': '📂',
      'move': '📌',
      'copy': '📋'
    };
    return icons[type] || '📄';
  };

  const getActivityColor = (type) => {
    const colors = {
      'upload': '#007bff',
      'download': '#28a745',
      'delete': '#dc3545',
      'create_folder': '#6f42c1',
      'move': '#fd7e14',
      'copy': '#20c997'
    };
    return colors[type] || '#6c757d';
  };

  const clearLiveFeed = () => {
    setWebdavLiveFeed([]);
  };

  const reloadRecentLogs = () => {
    loadLatest30Logs();
  };

  const clearLogFilters = () => {
    setWebdavLogFilters({
      user: '',
      type: '',
      startDate: '',
      endDate: ''
    });
  };

  // === EMPTY DEVICES: Selection & Delete Handlers ===
  const toggleDeviceSelection = (deviceKey) => {
    setSelectedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceKey)) {
        newSet.delete(deviceKey);
      } else {
        newSet.add(deviceKey);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDevices.size === emptyDevices.length && emptyDevices.length > 0) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(emptyDevices.map(d => `${d.owner_username || 'no-owner'}:${d.device_name}`)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDevices.size === 0) {
      alert('Please select at least one device to delete');
      return;
    }

    const count = selectedDevices.size;
    const deviceList = Array.from(selectedDevices).map(key => key.split(':')[1]).join(', ');
    
    if (!window.confirm(`⚠️ WARNING: Are you sure you want to delete ${count} device${count > 1 ? 's' : ''}?\n\nDevices: ${deviceList}\n\nThis action cannot be undone!`)) {
      return;
    }

    setEmptyLoading(true);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const deviceKey of selectedDevices) {
      const deviceName = deviceKey.split(':')[1];
      try {
        const response = await fetch(`/api/devices/${deviceName}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
          const errorText = await response.text();
          errors.push(`${deviceName}: ${errorText}`);
          console.error(`Failed to delete ${deviceName}:`, errorText);
        }
      } catch (error) {
        failCount++;
        errors.push(`${deviceName}: ${error.message}`);
        console.error(`Error deleting ${deviceName}:`, error);
      }
    }

    setEmptyLoading(false);
    setSelectedDevices(new Set());
    
    let message = '';
    if (successCount > 0) {
      message = `✅ Successfully deleted ${successCount} device${successCount > 1 ? 's' : ''}`;
    }
    if (failCount > 0) {
      message += `${message ? '\n' : ''}❌ Failed to delete ${failCount} device${failCount > 1 ? 's' : ''}`;
      if (errors.length > 0 && errors.length <= 3) {
        message += '\n\nErrors:\n' + errors.join('\n');
      }
    }
    
    alert(message || 'Operation completed');
    
    if (successCount > 0) {
      await fetchEmptyDevices(); // Refresh the list
    }
  };

  const handleContextMenu = (e, deviceKey) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      deviceKey
    });
  };

  const handleLongPressStart = (e, deviceKey) => {
    const timer = setTimeout(() => {
      const touch = e.touches[0];
      setContextMenu({
        x: touch.clientX,
        y: touch.clientY,
        deviceKey
      });
    }, 500); // 500ms long press
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const handleContextMenuDelete = async () => {
    if (contextMenu && contextMenu.deviceKey) {
      const wasSelected = selectedDevices.has(contextMenu.deviceKey);
      if (!wasSelected) {
        setSelectedDevices(new Set([contextMenu.deviceKey]));
      }
      closeContextMenu();
      // Delay to allow selection update
      setTimeout(() => handleDeleteSelected(), 100);
    }
  };

  // Close context menu on click anywhere
  useEffect(() => {
    const handleClickOutside = () => closeContextMenu();
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Clear selection when changing tabs
  useEffect(() => {
    setSelectedDevices(new Set());
    setContextMenu(null);
  }, [webdavCurrentTab]);

  // Effect to fetch history when tab changes or filters change
  useEffect(() => {
    if (webdavCurrentTab === 'history') {
      fetchWebdavLogs();
    }
  }, [webdavCurrentTab, webdavLogFilters, token]);

  // Auto-refresh effect - always enabled, removed button
  useEffect(() => {
    const interval = setInterval(() => {
      if (webdavCurrentTab === 'history') {
        fetchWebdavLogs();
      }
      fetchWebdavStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [webdavCurrentTab, webdavLogFilters, token]);

  // Initial stats fetch and load latest 30 logs into live feed
  useEffect(() => {
    fetchWebdavStats();
    loadLatest30Logs();
    // Load last 3 days for history tab
    loadLast3DaysForHistory();
  }, [token]);

  // Function to load latest 30 logs (any date) into live feed
  const loadLatest30Logs = async () => {
    try {
      console.log('Loading latest 30 logs into live feed...');
      
      const response = await fetch(`${apiUrlBase}/admin/webdav-logs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.logs && data.logs.length > 0) {
          // Filter out 'browse' activities and take latest 30
          const recentLogs = data.logs
            .filter(log => log.type !== 'browse')
            .slice(0, 30)
            .map(log => ({
              id: log.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              method: log.method,
              path: log.path,
              ip: log.ip === '127.0.0.1' ? 'local' : log.ip,
              timestamp: log.timestamp,
              userAgent: log.userAgent,
              user: log.user || 'unknown',
              type: log.type || log.method?.toLowerCase() || 'unknown',
              size: log.size
            }));
          
          if (recentLogs.length > 0) {
            setWebdavLiveFeed(recentLogs);
            console.log(`Loaded ${recentLogs.length} latest logs into live feed`);
          }
        }
      }
    } catch (err) {
      console.error('Error loading latest 30 logs into live feed:', err);
    }
  };

  // Function to load last 3 days logs for history tab
  const loadLast3DaysForHistory = async () => {
    try {
      console.log('Loading last 3 days logs for history...');
      
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      
      const filters = {
        startDate: threeDaysAgo.toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
      };
      
      // Set initial filters for history tab
      setWebdavLogFilters(filters);
      
    } catch (err) {
      console.error('Error setting up last 3 days filter:', err);
    }
  };

  useEffect(() => {
    if (webdavCurrentTab === 'empty' && emptyDevices.length === 0 && !emptyLoading) {
      fetchEmptyDevices();
    }
  }, [webdavCurrentTab]);


  // Enhanced WebSocket message handling - filter out 'browse'
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'webdav_activity' && data.payload) {
          // Skip 'browse' activities
          if (data.payload.type === 'browse' || data.payload.method === 'PROPFIND') {
            return;
          }
          
          const newActivity = {
            id: data.payload.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            method: data.payload.method,
            path: data.payload.path,
            ip: data.payload.ip === '127.0.0.1' ? 'local' : data.payload.ip,
            timestamp: data.payload.timestamp || new Date().toISOString(),
            userAgent: data.payload.userAgent,
            user: data.payload.user || 'unknown',
            type: data.payload.type || data.payload.method?.toLowerCase() || 'unknown',
            size: data.payload.size
          };
          
          setWebdavLiveFeed(prev => {
            const exists = prev.some(item => 
              item.id === newActivity.id ||
              (item.timestamp === newActivity.timestamp && 
               item.user === newActivity.user && 
               item.path === newActivity.path)
            );
            
            if (exists) {
              return prev;
            }
            
            return [newActivity, ...prev].slice(0, 100);
          });
          
          setWebdavStats(prev => ({
            ...prev,
            todayActivityCount: prev.todayActivityCount + 1,
            lastChecked: new Date().toISOString()
          }));
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    if (window.wsConnection) {
      window.wsConnection.removeEventListener('message', handleWebSocketMessage);
    }

    if (window.wsConnection && window.wsConnection.readyState === 1) {
      window.wsConnection.addEventListener('message', handleWebSocketMessage);
    } else {
      const checkConnection = setInterval(() => {
        if (window.wsConnection && window.wsConnection.readyState === 1) {
          window.wsConnection.addEventListener('message', handleWebSocketMessage);
          clearInterval(checkConnection);
        }
      }, 1000);
      
      setTimeout(() => clearInterval(checkConnection), 10000);
    }
    
    return () => {
      if (window.wsConnection) {
        window.wsConnection.removeEventListener('message', handleWebSocketMessage);
      }
    };
  }, []);

  return (
    <div className="dav-activity-container">
      <div className="monitor-tabs">
        <button 
          className={`tab-button ${webdavCurrentTab === 'live' ? 'active' : ''}`}
          onClick={() => setWebdavCurrentTab('live')}
        >
          Live Feed ({webdavLiveFeed.length})
        </button>
        <button 
          className={`tab-button ${webdavCurrentTab === 'history' ? 'active' : ''}`}
          onClick={() => setWebdavCurrentTab('history')}
        >
          Log History ({webdavLogHistory.length})
        </button>
		<button
  className={`tab-button ${webdavCurrentTab === 'empty' ? 'active' : ''}`}
  onClick={() => setWebdavCurrentTab('empty')}
>
  Empty Devices ({emptyDevices.length})
</button>

      </div>

      {webdavCurrentTab === 'history' && (
        <div className="monitor-filters">
          <label>
            User:
            <select 
              value={webdavLogFilters.user} 
              onChange={(e) => setWebdavLogFilters(prev => ({...prev, user: e.target.value}))}
            >
              <option value="">All Users</option>
              {availableUsers.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </label>
          
          <label>
            Type:
            <select 
              value={webdavLogFilters.type} 
              onChange={(e) => setWebdavLogFilters(prev => ({...prev, type: e.target.value}))}
            >
              <option value="">All Types</option>
              {availableTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </label>
          
          <label>
            From:
            <input 
              type="date" 
              value={webdavLogFilters.startDate}
              onChange={(e) => setWebdavLogFilters(prev => ({...prev, startDate: e.target.value}))}
            />
          </label>
          
          <label>
            To:
            <input 
              type="date" 
              value={webdavLogFilters.endDate}
              onChange={(e) => setWebdavLogFilters(prev => ({...prev, endDate: e.target.value}))}
            />
          </label>
          
          <button onClick={clearLogFilters} style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px'
          }}>
            Clear Filters
          </button>
        </div>
      )}

      <div className="activity-list">
  {/* LIVE */}
  {webdavCurrentTab === 'live' && (
    <>
      {webdavLiveFeed.length > 0 ? (
        webdavLiveFeed
          .filter((activity, index, self) => 
            index === self.findIndex(a => a.id === activity.id)
          )
          .map((activity) => (
            <div key={`live-${activity.id}-${activity.timestamp}`} className="activity-item" style={{
              borderLeft: `4px solid ${getActivityColor(activity.type)}`,
              backgroundColor: '#f8f9fa',
              margin: '2px 0',
              padding: '8px 12px',
              borderRadius: '0 4px 4px 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="activity-icon">{getActivityIcon(activity.type)}</span>
                <span className="activity-time" style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '11px',
                  color: '#6c757d',
                  minWidth: '60px'
                }}>
                  {formatWebdavActivityTime(activity.timestamp)}
                </span>
                <span style={{
                  backgroundColor: getActivityColor(activity.type),
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}>
                  {activity.type}
                </span>
                <span className="activity-text" style={{ flex: 1 }}>
                  <span dangerouslySetInnerHTML={{ __html: formatWebdavPath(activity.path, activity.user) }} />
                  {activity.size && (
                    <span style={{ color: '#6c757d', fontSize: '11px', marginLeft: '8px' }}>
                      ({(activity.size / 1024).toFixed(1)}KB)
                    </span>
                  )}
                </span>
                {activity.ip && activity.ip !== 'local' && (
                  <span style={{ fontSize: '11px', color: '#6c757d' }}>
                    {activity.ip}
                  </span>
                )}
              </div>
            </div>
          ))
      ) : (
        <div className="no-activity">Waiting for live activities...</div>
      )}
    </>
  )}

  {/* HISTORY */}
  {webdavCurrentTab === 'history' && (
    <>
      {isWebdavLoading ? (
        <div className="loading">Loading log history...</div>
      ) : webdavMonitorError ? (
        <div style={{ color: '#dc3545', textAlign: 'center', padding: '20px' }}>
          Error: {webdavMonitorError}
          <br />
          <button onClick={() => fetchWebdavLogs()} style={{
            marginTop: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 12px'
          }}>
            Retry
          </button>
        </div>
      ) : webdavLogHistory.length > 0 ? (
        webdavLogHistory
          .filter((log, index, self) => 
            index === self.findIndex(l => (l.id || l.timestamp) === (log.id || log.timestamp))
          )
          .map((log) => (
            <div key={`history-${log.id || log.timestamp}-${Math.random()}`} className="activity-item" style={{
              borderLeft: `4px solid ${getActivityColor(log.type)}`,
              backgroundColor: '#fff',
              margin: '1px 0',
              padding: '6px 10px',
              borderRadius: '0 4px 4px 0',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="activity-icon">{getActivityIcon(log.type)}</span>
                <span className="activity-time" style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '10px',
                  color: '#6c757d',
                  minWidth: '80px'
                }}>
                  {formatWebdavActivityTime(log.timestamp)}
                </span>
                <span style={{
                  backgroundColor: getActivityColor(log.type),
                  color: 'white',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  minWidth: '50px',
                  textAlign: 'center'
                }}>
                  {log.type}
                </span>
                <span className="activity-text" style={{ flex: 1 }}>
                  <span dangerouslySetInnerHTML={{ __html: formatWebdavPath(log.path, log.user) }} />
                  {log.size && (
                    <span style={{ color: '#6c757d', fontSize: '11px', marginLeft: '8px' }}>
                      ({(log.size / 1024).toFixed(1)}KB)
                    </span>
                  )}
                </span>
              </div>
            </div>
          ))
      ) : (
        <div className="no-activity">No log entries found for the selected criteria.</div>
      )}
    </>
  )}

  {/* EMPTY DEVICES */}
  {webdavCurrentTab === 'empty' && (
    <div className="empty-devices">
      {emptyLoading && <div className="loading">Scanning…</div>}
      {emptyError && <div className="error">Error: {emptyError}</div>}
      {!emptyLoading && !emptyError && (
        emptyDevices.length === 0 ? (
          <div className="no-data">Great! No empty device folders found.</div>
        ) : (
          <>
            {/* Toolbar with selection info and delete button */}
            <div className="empty-devices-toolbar">
              <div className="selection-info">
                {selectedDevices.size > 0 && (
                  <span className="selected-count">
                    {selectedDevices.size} device{selectedDevices.size > 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
                disabled={selectedDevices.size === 0}
                title={selectedDevices.size === 0 ? 'Select devices to delete' : `Delete ${selectedDevices.size} selected device${selectedDevices.size > 1 ? 's' : ''}`}
              >
                🗑️ Delete Selected ({selectedDevices.size})
              </button>
            </div>

            {/* Enhanced table with checkboxes */}
            <table className="table empty-devices-table">
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedDevices.size === emptyDevices.length && emptyDevices.length > 0}
                      onChange={toggleSelectAll}
                      title="Select all"
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                  </th>
                  <th>#</th>
                  <th>Device</th>
                  <th>Owner</th>
                  <th>Folder Path</th>
                </tr>
              </thead>
              <tbody>
                {emptyDevices.map((d, idx) => {
                  const deviceKey = `${d.owner_username || 'no-owner'}:${d.device_name}`;
                  const isSelected = selectedDevices.has(deviceKey);
                  return (
                    <tr
                      key={deviceKey}
                      className={isSelected ? 'selected-row' : ''}
                      onContextMenu={(e) => handleContextMenu(e, deviceKey)}
                      onTouchStart={(e) => handleLongPressStart(e, deviceKey)}
                      onTouchEnd={handleLongPressEnd}
                      onTouchMove={handleLongPressEnd}
                    >
                      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDeviceSelection(deviceKey)}
                          style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                        />
                      </td>
                      <td>{idx + 1}</td>
                      <td><strong>{d.device_name}</strong></td>
                      <td>{d.owner_username || '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{d.folder_path || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="context-menu-item context-menu-delete"
            onClick={handleContextMenuDelete}
          >
            🗑️ Delete Device
            {selectedDevices.size > 1 && selectedDevices.has(contextMenu.deviceKey) && ` (${selectedDevices.size} selected)`}
          </div>
        </div>
      )}
    </div>
  )}
</div>
</div>
  );
};

export default DavActivity;